import React from "react";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { useFrame } from "../../shared/frame";
import { lag } from "./lag";

import styles from "./index.module.scss";

interface Props {
    /** Whether the pointer is over a card's image */
    active: boolean;
}

/**
 * The "View case study" disc that follows the pointer over a card's image
 * (featuredwork.js). It trails the pointer on its own spring and grows as it
 * appears, so it reads as an object rather than a cursor. Hidden where there
 * is no hover; the card link carries the accessible name.
 */
export const CaseStudyCursor = (props: Props) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const state = React.useRef({
        x: 0,
        y: 0,
        tx: 0,
        ty: 0,
        on: 0,
        seen: false,
    });
    React.useEffect(() => {
        const cs = state.current;
        const onMove = (ev: PointerEvent) => {
            cs.tx = ev.clientX;
            cs.ty = ev.clientY;
            if (!cs.seen) {
                cs.x = cs.tx;
                cs.y = cs.ty;
                cs.seen = true;
            }
        };
        window.addEventListener("pointermove", onMove, { passive: true });
        return () => window.removeEventListener("pointermove", onMove);
    }, []);

    // Runs only while it is shown or still easing out. The prototype follows
    // the pointer on every frame, so when it appears it is already there;
    // starting the loop from the pointer gives the same thing.
    const [running, setRunning] = React.useState(false);
    React.useEffect(() => {
        if (!props.active || running) return;
        const cs = state.current;
        cs.x = cs.tx;
        cs.y = cs.ty;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setRunning(true);
    }, [props.active, running]);

    useFrame((_now, dt) => {
        const el = ref.current;
        if (!el) return;
        const cs = state.current;
        const want = props.active ? 1 : 0;
        const follow = lag(0.14, dt);
        cs.x += (cs.tx - cs.x) * follow;
        cs.y += (cs.ty - cs.y) * follow;
        cs.on += (want - cs.on) * lag(0.16, dt);
        const scale = 0.55 + 0.45 * cs.on;
        el.style.transform = `translate3d(${cs.x.toFixed(1)}px, ${cs.y.toFixed(1)}px, 0) scale(${scale.toFixed(3)})`;
        if (!want && cs.on < 0.001) setRunning(false);
    }, running);

    return (
        <div
            ref={ref}
            className={concatClassNames([
                styles.cursor,
                props.active ? styles.cursorOn : "",
            ])}
            aria-hidden="true"
        >
            <span>
                View
                <br />
                case study
            </span>
        </div>
    );
};
